import { twMerge } from "tailwind-merge";
import { assertNever } from "./tsutils.js";


type ClassValue = string | null | false | ClassValue[];

function buildClassList(...args: ClassValue[]): string[] {
    const classes: string[] = [];
    for (const arg of args.values()) {
        if (!arg)
            continue;

        if (typeof arg === "string") {
            classes.push(arg);
        } else if (Array.isArray(arg)) {
            classes.push(...buildClassList(...arg));
        } else {
            return assertNever(arg);
        }
    }
    return classes;
}

/**
 * Joins CSS class names, then merges conflicting Tailwind utilities so the
 * last one wins: cn("p-6", className) lets a caller's "p-3" replace the
 * default. Accepts strings, null, false, and nested arrays of these types.
 * Falsy values (null, false, empty strings) are filtered out to enable
 * conditional classes.
 *
 * IMPORTANT: This function does NOT accept `undefined` values. This is intentional
 * to force explicit decision-making when constructing class names. By rejecting
 * undefined at the type level, we catch potential bugs where a class name variable
 * might be accidentally undefined due to typos, missing imports, or logic errors.
 * This helps prevent situations where classes are silently missing from elements,
 * which could cause styling bugs that are difficult to debug.
 *
 * Use `null` or `false` for conditional classes instead of undefined:
 * @example
 * // ✓ Good - explicit conditional class
 * cn("btn", isActive ? "active" : null)
 * cn("btn", isActive && "active")
 * cn("btn", classNameOrUndefined ?? null)
 *
 * // ✗ Bad - TypeScript error if class might be undefined
 * cn("btn", classNameOrUndefined)  // Errors
 */
export function cn(...args: ClassValue[]): string {
    return twMerge(buildClassList(...args).join(" "));
}
